import {
  SetStateAction,
  Dispatch,
  createContext,
  useState,
  useContext,
  ReactNode,
} from 'react';

import { AppRole, ResponsibleParty } from '../../../insights/types/RoleTypes';
import { BaseApplicationProperty } from '../types/updateApplication/BaseApplicationProperty';
import { SelectFieldOptions } from '../types/appBulkEditTypes';

interface AppBulkEditProviderProps {
  children: ReactNode;
}

interface AppBulkEditContextType {
  field: SelectFieldOptions;
  setField: (value: SelectFieldOptions) => void;
  selectedParty: ResponsibleParty | undefined;
  setSelectedParty: Dispatch<SetStateAction<ResponsibleParty>>;
  selectedRole: AppRole;
  setSelectedRole: Dispatch<SetStateAction<AppRole>>;
  addSelectedRole: (role: AppRole) => void;
  property: BaseApplicationProperty;
  setProperty: Dispatch<SetStateAction<BaseApplicationProperty>>;
  resetBulkEditContext: () => void;
}

const AppBulkEditContext = createContext<AppBulkEditContextType | undefined>(
  undefined,
);

/**
 * Provides the selected apps context to its children.
 * @param AppBulkEditProviderProps props - The props for the provider.
 */
export const AppBulkEditProvider = ({
  children,
}: AppBulkEditProviderProps): JSX.Element => {
  const [field, setField] = useState<SelectFieldOptions>(
    SelectFieldOptions.APP_GROUP,
  );
  const [property, setProperty] = useState<BaseApplicationProperty>(
    {} as BaseApplicationProperty,
  );
  const [selectedParty, setSelectedParty] = useState<ResponsibleParty>(
    {} as ResponsibleParty,
  );
  const [selectedRole, setSelectedRole] = useState<AppRole>({} as AppRole);

  const resetBulkEditContext = () => {
    setField(SelectFieldOptions.APP_GROUP);
    setSelectedParty({} as ResponsibleParty);
    setSelectedRole({} as AppRole);
  };

  const addSelectedRole = (role: AppRole) => {
    setSelectedRole(role);
  };
  return (
    <AppBulkEditContext.Provider
      value={{
        property,
        setProperty,
        field,
        selectedParty,
        selectedRole,
        setSelectedRole,
        addSelectedRole,
        setField,
        setSelectedParty,
        resetBulkEditContext,
      }}
    >
      {children}
    </AppBulkEditContext.Provider>
  );
};

/**
 * Custom hook to use the AppBulkEditContext.
 * @returns AppBulkEditContextType The context value.
 */
export const useAppBulkEditContext = (): AppBulkEditContextType => {
  const context = useContext(AppBulkEditContext);
  if (!context) {
    throw new Error(
      'useAppBulkEditContext must be used within a AppBulkEditProvider',
    );
  }
  return context;
};
